import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";

import dayjs from "utility/dayjs";
import { notifyError, notifySuccess } from "utility/notify";

const FindingBulkActionsStateDialog = memo(
  function OrganizationFindingBulkActions({
    findings,
    type,
    onClose,
  }: FindingBulkActionsStateDialogProps): JSX.Element | null {
    const [, findingActions] = useFindingStore();

    const [loading, setLoading] = useState(false);

    if (findings == null || !type) {
      return null;
    }

    const confirm = () => {
      setLoading(true);
      const uuids = findings.map((finding) => finding.uuid);

      findingActions.bulkUpdateState({
        uuids,
        stateType: type,
        onSuccess,
        onError,
      });
    };

    const onSuccess = () => {
      setLoading(false);
      onClose();
      notifySuccess(`Successfully ${`${type}d`} the selected findings`);
    };

    const onError = () => {
      setLoading(false);
      onClose();
      notifyError(
        `Something went wrong trying to ${type} one or more of ` +
          `the selected findings`
      );
    };

    const getDialogTitle = () => {
      switch (type) {
        case "ignore":
          return "Ignore Selected Findings";
        case "unignore":
          return "Unignore Selected Findings";
        case "acknowledge":
          return "Acknowledge Selected Findings";
        default:
          return "";
      }
    };

    return (
      <Dialog open={true} onClose={onClose} fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This will {type} the findings that you have selected. It will skip
            over any findings that cannot be {type}d.
          </DialogContentText>
          <Typography sx={{ mt: 2 }} fontWeight="bold">
            Findings that will be {type}d:
          </Typography>
          <ul>
            {(findings || []).map((finding) => {
              return (
                <li key={finding.uuid}>
                  <Typography>{`${finding.name} from ${dayjs(
                    finding.create_time
                  ).format("MM/DD/YYYY")}`}</Typography>
                </li>
              );
            })}
          </ul>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton loading={loading} onClick={confirm}>
            {type} selected findings
          </LoadingButton>
        </DialogActions>
      </Dialog>
    );
  }
);

export default FindingBulkActionsStateDialog;

// -----------------------------------------------------------------------------

interface FindingBulkActionsStateDialogProps {
  findings?: Finding[];
  type?: "ignore" | "unignore" | "acknowledge";
  onClose: () => void;
}
